import React, { useState } from 'react';
import { useItems } from 'hooks/atoms/items-hook';
import CollectionKeys from 'hooks/molecules/collection-keys';
import { Grid, Card, CardContent, Divider, makeStyles,
  Typography, Tabs, Tab, Box } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import axiosClient from 'http/axios-client';
import { useQuery } from 'react-query';
import AppLoader from '../../basic/AppLoader';
import getTheme from '../../../theme/theme';
import DisableAppointmentSwitch from './DisableAppointmentSwitch';
import { parseDate, getFullRegistrationStatus } from '../consents/ConsentsTable';

const useStyles = makeStyles((theme) => {
  return {
    borderedTypography: {
      border: '1px solid',
      borderColor: 'theme.palette.secondary.light',
      padding: theme.spacing(1),
    },

    customTabs: {
      '& .MuiTab-root': {
        fontSize: '18px',
        color: 'rgb(100, 141, 174)',
      },
    },

  };
});

// formatting the data field
function FieldItem({ title, field }) {
  const classes = useStyles(getTheme());

  return (
    <>
      <Typography variant='subtitle1' color={ getTheme().palette.secondary.main }>
        {title}
      </Typography>
      <Typography
        className={ classes.borderedTypography }
        variant='h6'
        style={ { marginBottom: '10px', minHeight: '45px' } }
        fullWidth
      >
        {field }
      </Typography>
      <Divider />
    </>
  );
}

// for tabs selection
function a11yProps(index) {
  return {
    id: `simple-tab-${ index }`,
    'aria-controls': `simple-tabpanel-${ index }`,
  };
}

// Function for Tabs
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={ value !== index }
      id={ `simple-tabpanel-${ index }` }
      aria-labelledby={ `simple-tab-${ index }` }
      { ...other }
    >
      {value === index && (
        <Box p={ 3 }>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

// Component
function SerialNumberDetails({ serialNumber }) {
  const classes = useStyles();
  const [ tab, setTab ] = useState(0);

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };

  // The Request
  const {
    itemsLoading: kitLoading,
    items: kitsData,
    countFiltered,
    refetch,
    countFilteredIsLoading,
  } = useItems(CollectionKeys.REGISTRATION_KITS, { filterQuery: {
    _where: [
      [{ 'serial_number_id.serial': serialNumber }],
    ],
  },
  queryOptions: { cacheTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true } });

  const { data: bookedAppointments, isLoading: isBookedAppointmentsLoading } = useQuery(
    [ 'Appointment', 'booked', serialNumber ], () => {
      return axiosClient.get('/appointments/getTimeSlots', { params: { serial_number: serialNumber, serial_number_eq: serialNumber } });
    },
  );

  if (kitLoading || countFilteredIsLoading || isBookedAppointmentsLoading) {
    return <AppLoader />;
  }
  let appointmentDate = '';
  if (bookedAppointments.length > 0) {
    const date = new Date(bookedAppointments[0].date_from);
    appointmentDate = `${ date.getDate() }/${ date.getMonth() + 1 }/${ date.getFullYear() } ${ date.getHours() }:${ date.getMinutes().toString().padStart(2, '0') }`;
  }

  const kit = kitsData[0];

  // format address
  let addr = '';
  if (!countFilteredIsLoading && countFiltered === 1) {
    try {
      if (typeof kit?.customer_id?.address === 'string') {
        addr = JSON.parse(kit.customer_id?.address);
        addr = `${ addr?.streetAddress }, ${ addr?.city }, ${ addr?.postalCode }`;
      } else if (typeof kit.customer_id.address === 'object') {
        addr = `${ kit.customer_id.address.streetAddress }, 
        ${ kit.customer_id.address?.city },
         ${ kit.customer_id.address?.postalCode }`;
      }
    } catch (error) {
      addr = '';
    }
  }

  // format register from
  let register = '';
  if (kit === undefined) {
    register = '';
  } else if (kit?.business !== null) {
    register = 'Corporate';
  } else if (kit?.doctor !== null) {
    register = 'Doctor';
  } else if (kit?.business === null && kit?.doctor === null) {
    register = 'Individual';
  }

  // format Consents
  let consents = [];
  if (kit?.agreements !== undefined && kit?.agreements.length > 0) {
    consents = kit?.agreements.map((item) => {
      return item.consent.name;
    });
  }

  return (
    <div>
      {countFiltered === 0
        && (
          <Alert severity='error'>
            <AlertTitle>
              {`Δεν βρέθηκαν αποτελέσματα για το serial number: ${ serialNumber }`}
            </AlertTitle>
          </Alert>
        )}
      {kitsData
      && (
        <Box pt={ 2 }>
          <Box p={ 2 }>
            <Typography variant='h5'>
              {'Registered from: '}
              <span style={ { color: getTheme().palette.secondary.main, fontWeight: 700 } }>{register}</span>
            </Typography>
          </Box>
          <Tabs
            className={ classes.customTabs }
            value={ tab }
            onChange={ handleChange }
            aria-label='simple tabs example'
            indicatorColor='secondary'
          >
            <Tab label='Basic Details' { ...a11yProps(0) } />
            <Tab label='Registration and Report' { ...a11yProps(1) } />
            <Tab label='Invitation and Consents' { ...a11yProps(2) } />
          </Tabs>
          <TabPanel value={ tab } index={ 0 }>
            <Grid container item xs={ 12 } spacing={ 3 }>
              {kit?.doctor !== null
                    && (
                      <Grid item xs={ 6 }>
                        <Card>
                          <CardContent>

                            <Typography variant='h5' color='secondary'>Doctor Details</Typography>
                            <FieldItem
                              title='Last Name'
                              field={ kit?.doctor?.family_name }
                            />

                            <FieldItem
                              title='First Name'
                              field={ kit?.doctor?.name }
                            />

                            <FieldItem
                              title='Email'
                              field={ kit?.doctor?.email }
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
              {kit?.business !== null && (
                <Grid item xs={ 6 }>
                  <Card>
                    <CardContent>

                      <Typography variant='h5' color='secondary'>Corporate Details</Typography>
                      <FieldItem
                        title='Brand Name'
                        field={ kit?.business?.brand_name }
                      />
                      <FieldItem
                        title='Email'
                        field={ kit?.business?.email }
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
              <Grid item xs={ 6 }>
                <Card>
                  <CardContent>
                    <Typography
                      variant='h5'
                      color='secondary'
                    >
                      {kit?.child_id !== null ? 'Personal Details' : 'Patient Details'}
                    </Typography>

                    <FieldItem
                      title='Last Name'
                      field={ kit?.customer_id?.family_name }
                    />
                    <FieldItem
                      title='First Name'
                      field={ kit?.customer_id?.name }
                    />

                    <FieldItem
                      title='Date of Birth'
                      field={ parseDate(kit?.customer_id?.birthdate) }
                    />

                    <FieldItem
                      title='Biological Gender'
                      field={ typeof kit?.customer_id?.gender === 'string' ? kit?.customer_id?.gender.toUpperCase() : '' }
                    />

                    <FieldItem
                      title='Address'
                      field={ addr }
                    />

                    <FieldItem
                      title='Country'
                      field={ kit?.customer_id?.country }
                    />

                    <FieldItem
                      title='Email'
                      field={ kit?.customer_id?.email }
                    />

                    <FieldItem
                      title='Mobile Number'
                      field={ kit?.customer_id?.phone_number }
                    />

                    <FieldItem
                      title='Landline Number'
                      field={ kit?.customer_id?.phoneNumber }
                    />
                  </CardContent>
                </Card>
              </Grid>
              {(kit?.child_id !== null || register === 'Individual')
              && (
                <Grid item xs={ 6 }>
                  { register === 'Individual'

            && (
              <Card style={ { marginBottom: '20px' } }>
                <CardContent>
                  <Typography variant='h5' color='secondary'>Appointment Availability</Typography>
                  <Typography>
                    Disabled
                    <DisableAppointmentSwitch
                      key={ serialNumber }
                      appointmentAvailable={ kit?.serial_number_id?.appointment_availability }
                      id={ kit?.serial_number_id?.id }
                      refetch={ refetch }
                    />
                    Enabled
                  </Typography>
                </CardContent>
              </Card>
            )}
                  {kit?.child_id !== null
            && (
              <Card>
                <CardContent>
                  <Typography
                    variant='h5'
                    color='secondary'
                  >
                    Patient Details
                  </Typography>

                  <FieldItem
                    title='Last Name'
                    field={ kit?.child_id?.family_name }
                  />
                  <FieldItem
                    title='First Name'
                    field={ kit?.child_id?.name }
                  />

                  <FieldItem
                    title='Date of Birth'
                    field={ parseDate(kit?.child_id?.birthdate) }
                  />

                  <FieldItem
                    title='Biological Gender'
                    field={ typeof kit?.child_id?.gender === 'string' ? kit?.child_id?.gender.toUpperCase() : '' }
                  />

                  <FieldItem
                    title='Country'
                    field={ kit?.child_id?.country }
                  />

                  <FieldItem
                    title='Mobile Number'
                    field={ kit?.child_id?.phone_number }
                  />

                  <FieldItem
                    title='Landline Number'
                    field={ kit?.child_id?.house_number }
                  />
                </CardContent>
              </Card>
            )}
                </Grid>
              )}
            </Grid>
          </TabPanel>
          <TabPanel value={ tab } index={ 1 }>
            <Grid container item xs={ 12 } spacing={ 3 }>
              <Grid item xs={ 6 }>
                <Card>
                  <CardContent>
                    <Typography variant='h5' color='secondary'>Registration</Typography>

                    <FieldItem
                      title='Registration Status'
                      field={ typeof kit?.registrationStatus === 'string' ? getFullRegistrationStatus(kit) : '' }
                    />

                    <FieldItem
                      title='Registration Done Date'
                      field={ parseDate(kit?.metadata?.registrationDoneDate) }
                    />

                    <FieldItem
                      title='Appointment Availability'
                      field={ bookedAppointments.length > 0 ? `Booked for date: ${ appointmentDate }`
                        : kit?.serial_number_id?.appointment_availability === true ? 'Not Booked Yet' : 'Not available' }
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={ 6 }>
                <Card>
                  <CardContent>
                    <Typography variant='h5' color='secondary'>Sample</Typography>
                    <FieldItem
                      title='Sample Received Date'
                      field={ parseDate(kit?.metadata?.sampleReceivedDate) || <Typography color='textSecondary'>Not Found</Typography> }
                    />
                    <FieldItem
                      title='Sample Collection Date'
                      field={ parseDate(kit?.metadata?.sampleCollectionDate) || <Typography color='textSecondary'>Not Found</Typography> }
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={ 6 }>
                <Card>
                  <CardContent>
                    <Typography variant='h5' color='secondary'>Payment</Typography>
                    <FieldItem
                      title='Report Paid'
                      field={ typeof kit?.serial_number_id?.report_payment_status === 'string' ? kit?.serial_number_id?.report_payment_status.toUpperCase() : '' }
                    />
                    <FieldItem
                      title='Kit Paid'
                      field={ typeof kit?.serial_number_id?.kit_payment_status === 'string' ? kit?.serial_number_id?.kit_payment_status.toUpperCase() : '' }
                    />
                    <FieldItem
                      title='Payment Amount'
                      field={ kit?.metadata?.cashAmount }
                    />
                    <FieldItem
                      title='Invoice Number'
                      field={ kit?.metadata?.invoiceNumber }
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={ 6 }>
                <Card>
                  <CardContent>
                    <Typography variant='h5' color='secondary'>Report</Typography>
                    <FieldItem
                      title='Report language'
                      field={ (kit?.locale === 2 && 'el-GR') || (kit?.locale === 3 && 'en-US') }
                    />
                    <FieldItem
                      title='Report status'
                      field={ kit?.pdf_available ? (
                        <>
                          <span>Available</span>
                          <DoneIcon style={ { color: getTheme().palette.secondary.main } } />
                        </>
                      ) : (
                        <>
                          <span>Not Available</span>
                          <CloseIcon style={ { color: getTheme().palette.error.main } } />
                        </>
                      ) }
                    />
                    <FieldItem
                      title='Pdf Report Creation Date'
                      field={ parseDate(kit?.pdf_creation) || <Typography color='textSecondary'>Not Found</Typography> }
                    />
                    <FieldItem
                      title='Pdf Received Date'
                      field={ parseDate(kit?.pdf_received_date) || <Typography color='textSecondary'>Not Found</Typography> }
                    />
                    <FieldItem
                      title='Report Delivery Date'
                      field={ parseDate(kit?.report_delivery_date) || <Typography color='textSecondary'>Not Found</Typography> }
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={ tab } index={ 2 }>
            <Grid container item xs={ 12 } spacing={ 3 }>
              {Array.isArray(kit?.invitations) && typeof kit?.invitations[0]?.status === 'string' && kit?.invitations.map((invitation) => {
                const space = ' ';
                return (
                  <Grid item xs={ 6 } key={ kit.id }>
                    <Card>
                      <CardContent>
                        <Typography variant='h5' color='secondary'>
                          Invitation
                        </Typography>
                        <FieldItem
                          title='Invitation Type'
                          field={ invitation?.reason === 'pgx-doctor-consent' ? 'PGX Doctor Consent' : 'Healthcare Professional Share Results' }
                        />
                        <FieldItem
                          title='Invitation Status'
                          field={ invitation?.status.toUpperCase() || '' }
                        />
                        <FieldItem
                          title='Doctor Fullname who has been sent an invitation'
                          field={ invitation?.metadata?.firstName + space + invitation?.metadata?.lastName || '' }
                        />
                        <FieldItem
                          title='Doctor Specialty who has been sent an invitation'
                          field={ invitation?.metadata?.specialty || '' }
                        />
                        <FieldItem
                          title='Doctor Email who has been sent an invitation'
                          field={ invitation?.metadata?.email || '' }
                        />
                        <FieldItem
                          title='Doctor Business number who has been sent an invitation'
                          field={ invitation?.metadata?.businessNumber || '' }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              {Array.isArray(kit?.invitations) && kit.invitations.length === 0
                                  && (
                                    <Grid item xs={ 6 } key={ kit.id }>
                                      <Card>
                                        <CardContent>
                                          <Typography variant='h5' color='secondary'>
                                            Invitation Not Found
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  )}

              <Grid item xs={ 6 }>
                <Card>
                  <CardContent>
                    <Typography variant='h5' color='secondary'>Consents</Typography>
                    {consents.length > 0 && (
                      consents.map((consent) => {
                        return (
                          <Typography
                            color='secondary'
                            variant='subtitle1'
                            key={
                              consent
                            }
                          >
                            <span style={ { color: 'black' } }>{consent.replaceAll('_', ' ')}</span>
                            {' '}
                            <DoneIcon />
                          </Typography>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

        </Box>
      )}

    </div>
  );
}

export default SerialNumberDetails;
